import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send, Loader2, CalendarCheck, Mail } from "lucide-react";
import { Reveal, Chapter, IMAGES, scrollToId } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const inputCls =
  "w-full rounded-xl border border-amber-900/15 bg-white/90 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-deepteal">
    {children}
  </label>
);

const Enrollment = () => {
  const [form, setForm] = useState({
    parent_name: "", phone: "", email: "", child_age: "", num_children: "1",
    start_date: "", days_needed: [], preferred_schedule: "", scholarship: "", message: "", website: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      days_needed: f.days_needed.includes(day)
        ? f.days_needed.filter((d) => d !== day)
        : [...f.days_needed, day],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.days_needed.length === 0) {
      toast.error("Please select at least one day of care needed.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Enrollment inquiry sent! Ms. Bre will be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong sending your inquiry. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enrollment" data-testid="enrollment-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-amber-200/40 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <Reveal>
              <Chapter num="07" label="Enrollment" />
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12] text-deepteal-dark">
                Looking for dependable childcare in <em className="italic text-terracotta">Halethorpe?</em>
              </h2>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
                We'd love to welcome your family to Pieces to Our Puzzle Childcare. Contact us today
                to learn about availability, enrollment, tuition, and child care scholarship options.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  data-testid="enrollment-cta-tour"
                  onClick={() => scrollToId("#contact")}
                  className="inline-flex items-center gap-2 rounded-full bg-deepteal px-6 py-3 text-sm font-bold text-amber-50 transition-all duration-300 hover:-translate-y-0.5 hover:bg-deepteal-dark"
                >
                  <CalendarCheck className="h-4 w-4" /> Schedule a Tour
                </button>
                <button
                  data-testid="enrollment-cta-contact"
                  onClick={() => scrollToId("#contact")}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-deepteal/20 bg-white/70 px-6 py-3 text-sm font-bold text-deepteal transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                >
                  <Mail className="h-4 w-4" /> Contact Us
                </button>
              </div>
              <div className="mt-10 hidden lg:block overflow-hidden rounded-3xl border-4 border-white shadow-2xl shadow-deepteal/15 rotate-[-1.5deg]">
                <img
                  src={IMAGES.arts}
                  alt="A Black child and a white child creating with modeling clay together — childcare for ages 2-12 in Halethorpe MD"
                  className="h-64 w-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-amber-900/10 bg-white/85 p-7 sm:p-9 shadow-2xl shadow-deepteal/12 backdrop-blur">
              {done ? (
                <div data-testid="enrollment-success" className="flex flex-col items-center py-14 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <CheckCircle2 className="h-9 w-9" />
                  </span>
                  <h3 className="mt-6 font-serif text-3xl text-deepteal-dark">Thank you!</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Your enrollment inquiry has been received. Ms. Bre will reach out to you shortly
                    to discuss availability, tours, and next steps.
                  </p>
                  <button
                    data-testid="enrollment-success-close"
                    onClick={() => setDone(false)}
                    className="mt-7 rounded-full border-2 border-deepteal/20 px-6 py-2.5 text-sm font-bold text-deepteal hover:border-terracotta hover:text-terracotta transition-colors"
                  >
                    Send Another Inquiry
                  </button>
                </div>
              ) : (
                <form data-testid="enrollment-form" onSubmit={submit}>
                  <p className="font-serif text-2xl text-deepteal-dark">Enrollment Inquiry</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Please don't include sensitive medical or financial information in this form.
                  </p>
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={set("website")}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="en-name">Parent/Guardian Name *</Label>
                      <input id="en-name" data-testid="enrollment-form-name" required value={form.parent_name} onChange={set("parent_name")} className={inputCls} placeholder="Your full name" />
                    </div>
                    <div>
                      <Label htmlFor="en-phone">Phone Number *</Label>
                      <input id="en-phone" data-testid="enrollment-form-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(410) 555-0123" />
                    </div>
                    <div>
                      <Label htmlFor="en-email">Email Address *</Label>
                      <input id="en-email" data-testid="enrollment-form-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                    </div>
                    <div>
                      <Label htmlFor="en-age">Child's Age *</Label>
                      <input id="en-age" data-testid="enrollment-form-child-age" required value={form.child_age} onChange={set("child_age")} className={inputCls} placeholder="e.g. 3, or 5 & 8" />
                    </div>
                    <div>
                      <Label htmlFor="en-count">Number of Children *</Label>
                      <select id="en-count" data-testid="enrollment-form-num-children" required value={form.num_children} onChange={set("num_children")} className={inputCls}>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4+">4+</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="en-start">Desired Start Date</Label>
                      <input id="en-start" data-testid="enrollment-form-start-date" type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Days of Care Needed *</Label>
                      <div className="flex flex-wrap gap-2" data-testid="enrollment-form-days">
                        {WEEK_DAYS.map((d) => (
                          <button
                            type="button"
                            key={d}
                            data-testid={`enrollment-day-${d.toLowerCase()}`}
                            onClick={() => toggleDay(d)}
                            className={`rounded-full border px-4 py-2 font-mono text-xs font-semibold transition-all duration-300 ${
                              form.days_needed.includes(d)
                                ? "border-terracotta bg-terracotta text-amber-50 shadow-md shadow-terracotta/25"
                                : "border-amber-900/20 bg-white text-slate-600 hover:border-terracotta/50 hover:text-terracotta"
                            }`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="en-schedule">Preferred Schedule</Label>
                      <select id="en-schedule" data-testid="enrollment-form-schedule" value={form.preferred_schedule} onChange={set("preferred_schedule")} className={inputCls}>
                        <option value="">Select one…</option>
                        <option>Full Day (6 AM–6 PM)</option>
                        <option>Before-School Care</option>
                        <option>After-School Care</option>
                        <option>Weekend Care</option>
                        <option>Flexible / Not Sure Yet</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="en-scholarship">Child Care Scholarship</Label>
                      <select id="en-scholarship" data-testid="enrollment-form-scholarship" value={form.scholarship} onChange={set("scholarship")} className={inputCls}>
                        <option value="">Select one…</option>
                        <option>Yes — I receive scholarship assistance</option>
                        <option>No</option>
                        <option>Applying / In Progress</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="en-message">Message or Special Requests</Label>
                      <textarea id="en-message" data-testid="enrollment-form-message" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your child, your schedule, or any questions…" />
                    </div>
                  </div>
                  <button
                    data-testid="enrollment-form-submit"
                    type="submit"
                    disabled={sending}
                    className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-4 text-sm font-bold text-amber-50 shadow-xl shadow-terracotta/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-deepteal disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                    {sending ? "Sending…" : "Start Enrollment"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Enrollment;
